import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import Home from "./pages/Home";
import About from "./pages/About";
import Products from "./pages/Products";
import Gallery from "./pages/Gallery";
import Contact from "./pages/Contact";

const SmoothScroll = ({ children }) => {
  const lenisRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    lenisRef.current = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    lenisRef.current?.scrollTo(0, { immediate: true });
  }, [location.pathname]);

  return children;
};

const PAGE_SEO = {
  "/": {
    title: "SOHOM | uPVC Windows & Doors Manufacturers in Hyderabad",
    description: "SOHOM fabricates premium uPVC windows and doors in Hyderabad — casement, sliding, tilt & turn, slide & fold and lift & slide systems. Soundproof up to 49 dB, thermal insulation 0.92 W/m²K.",
  },
  "/about": {
    title: "About SOHOM | uPVC Windows & Doors Manufacturer Since 2008",
    description: "From a 7,000 sq ft factory in 2008 to two German-machine plants today — SOHOM's journey as Hyderabad's fabrication-first uPVC windows and doors studio.",
  },
  "/products": {
    title: "uPVC Windows & Doors Catalogue | Casement, Sliding, Tilt & Turn — SOHOM",
    description: "Explore SOHOM's uPVC systems: casement doors & windows, tilt & turn, sliding, slide & fold and lift & slide — with real U-values, sound ratings and glass options.",
  },
  "/gallery": {
    title: "Installations Gallery | Real uPVC Projects in Hyderabad, Bengaluru & Goa — SOHOM",
    description: "20+ real SOHOM installations: villas, apartments, resorts and hospitals across Hyderabad, Secunderabad, Bengaluru, Tamil Nadu and Goa.",
  },
  "/contact": {
    title: "Contact SOHOM | Get a uPVC Windows & Doors Quote — Moosapet, Hyderabad",
    description: "Visit SOHOM's experience centre at 12-5-110, Moosapet, Hyderabad or call +91 8886600604 for a free quote on uPVC windows and doors.",
  },
};

const RouteSEO = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const meta = PAGE_SEO[pathname] || PAGE_SEO["/"];
    document.title = meta.title;
    document.querySelector('meta[name="description"]')?.setAttribute("content", meta.description);
    const canonicalUrl = `https://sohom.in${pathname === "/" ? "/" : pathname}`;
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;
    document.querySelector('meta[property="og:url"]')?.setAttribute("content", canonicalUrl);
    document.querySelector('meta[property="og:title"]')?.setAttribute("content", meta.title);
    document.querySelector('meta[property="og:description"]')?.setAttribute("content", meta.description);
  }, [pathname]);
  return null;
};

function App() {
  return (
    <div className="min-h-screen bg-obsidian text-slate-100">
      <BrowserRouter>
        <SmoothScroll>
          <RouteSEO />
          <Navbar />
          <main>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/products" element={<Products />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/contact" element={<Contact />} />
            </Routes>
          </main>
          <Footer />
        </SmoothScroll>
      </BrowserRouter>
      <Toaster theme="dark" position="bottom-right" richColors />
    </div>
  );
}

export default App;
